import type { ContractTemplate, ContractTemplateInput } from '@/types/portal';
import { contractTemplates as starterTemplates } from '@/mocks/contractTemplates';

const DB_NAME = 'cpaas-contracts';
const DB_VERSION = 1;
const STORE = 'templates';

/** In-memory fallback for environments where IndexedDB is unavailable. */
let memory: ContractTemplate[] = [];
let dbPromise: Promise<IDBDatabase | null> | null = null;

function getDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function readAll(db: IDBDatabase): Promise<ContractTemplate[]> {
  return new Promise((resolve) => {
    try {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      request.onsuccess = () => resolve((request.result as ContractTemplate[]) ?? []);
      request.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

function putOne(db: IDBDatabase, template: ContractTemplate): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = db.transaction(STORE, 'readwrite').objectStore(STORE).put(template);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

function removeOne(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Starter templates resolved with their origin tag. */
function starters(): ContractTemplate[] {
  return starterTemplates.map((template) => ({ ...template, origin: 'starter' as const }));
}

/** Brand-saved templates first (newest first), then the platform starters. */
export async function listContractTemplates(): Promise<ContractTemplate[]> {
  const db = await getDb();
  const saved = db ? await readAll(db) : memory;
  const ordered = [...saved].sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
  return [...ordered, ...starters()];
}

/** Persist the current contract as a reusable template. */
export async function saveContractTemplate(
  input: ContractTemplateInput,
): Promise<ContractTemplate> {
  const template: ContractTemplate = {
    ...input,
    id: `tpl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    savedAt: new Date().toISOString(),
    origin: 'custom',
  };
  const db = await getDb();
  if (db) await putOne(db, template);
  else memory = [template, ...memory];
  return template;
}

/** Remove a brand-saved template. Platform starters cannot be removed. */
export async function deleteContractTemplate(id: string): Promise<void> {
  if (!id.startsWith('tpl-') || id.startsWith('tpl-starter-')) return;
  const db = await getDb();
  if (db) {
    await removeOne(db, id);
    return;
  }
  memory = memory.filter((item) => item.id !== id);
}